import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import {
	Box,
	FormHelperText,
	Link as MuiLink,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
} from '@mui/material';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { SubmitHandler } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
	DEPRECATED__CLAIMS_ONLY_USER__ACCOUNT_TYPE,
	DEPRECATED__EMPLOYEE__ACCOUNT_TYPE,
	DEPRECATED__READ_ONLY_USER__ACCOUNT_TYPE,
	INTERNAL_ADMIN__ACCOUNT_TYPE,
} from '../../../context/auth';
import UserContext from '../../../context/userContext';
import { errorToast, successToast } from '../../Toast';
import { RolesApi, TenantsApi, UsersApi } from './api';
import { UsersForm } from './components';
import ConfirmationModal from './components/ConfirmationModal';
import {
	LEGACY_AUTH_TENANT_CLAIM_ONLY_LABEL,
	LEGACY_AUTH_TENANT_EMPLOYEE_LABEL,
	LEGACY_AUTH_TENANT_READ_ONLY_LABEL,
	USER_DETAILS_PAGE__BACK_BUTTON,
	USER_DETAILS_PAGE__DELETE_CONFIRMATION_TEXT,
	USER_DETAILS_PAGE__DELETE_CONFIRMATION_TEXT_2,
	USER_DETAILS_PAGE__DELETE_USER_BUTTON_ALT_TEXT,
	USER_DETAILS_PAGE__DELETE_USER_BUTTON_TOOLTIP,
	USER_DETAILS_PAGE__EDIT_USER_BUTTON_ALT_TEXT,
	USER_DETAILS_PAGE__EDIT_USER_BUTTON_TOOLTIP,
	USER_DETAILS_PAGE__EDIT_USER_MODAL_TITLE,
	USER_DETAILS_PAGE__EMAIL_TITLE,
	USER_DETAILS_PAGE__FILTER_TENANT_PLACEHOLDER,
	USER_DETAILS_PAGE__LEGACY_AUTH_TENANT_TITLE,
	USER_DETAILS_PAGE__NAMESPACE_TITLE,
	USER_DETAILS_PAGE__NAMESPACE_TITLE_EXPLAINER,
	USER_DETAILS_PAGE__NAME_TITLE,
	USER_DETAILS_PAGE__TENANTS_TITLE,
	USER_DETAILS_PAGE__TITLE,
	USER_DETAILS_PAGE__USERNAME_TITLE,
	USE_ROLE_ACCOUNT_TYPE,
} from './data/constants';
import { UserInfo, UserRequest } from './types';

interface RoleMap {
	[role: string]: string;
}

interface UserDetailRolesShape {
	rolesAndPermissionTenants: {
		[tenantName: string]: boolean;
	};
	legacyAuthTenants: {
		[tenantName: string]: string;
	};
	namespaces: {
		[namespace: string]: boolean;
	};
}

const AdministrationUserDetails = () => {
	let { userUuid } = useParams();
	const navigate = useNavigate();
	const [searchText, setSearchText] = React.useState('');
	const {
		selectedUserRole,
		user: { userUuid: activeUserUuid },
	} = React.useContext(UserContext);
	const queryClient = useQueryClient();

	const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
		React.useState(false);
	const [isEditConfirmationModalOpen, setIsEditConfirmationModalOpen] =
		React.useState(false);

	const USER_KEY = UsersApi.USERS_KEY + '-' + selectedUserRole.namespace;
	const ROLES_KEY = RolesApi.ROLES_KEY + '-' + selectedUserRole.namespace;
	const TENANTS_KEY = TenantsApi.TENANTS_KEY + '-' + selectedUserRole.namespace;

	const { data: user } = useQuery({
		queryKey: [USER_KEY, userUuid],
		queryFn: () => UsersApi.getUser(userUuid),
	});

	const { data: assignedRoles, isLoading: isLoadingAssignedRoles } = useQuery({
		queryKey: ['assignedRoles', userUuid],
		queryFn: () => UsersApi.getUserRoles(userUuid),
	});

	const { data: availableRoles, isLoading: isLoadingAvailableRoles } = useQuery(
		{
			queryKey: [ROLES_KEY],
			queryFn: RolesApi.getRoles,
		},
	);

	const { data: tenants, isLoading: isLoadingTenants } = useQuery({
		queryKey: [TENANTS_KEY],
		queryFn: TenantsApi.getTenants,
	});

	const assignRolesMutation = useMutation(UsersApi.assignRolesToUser, {
		onSuccess: (data) => {
			queryClient.invalidateQueries(['assignedRoles', userUuid]);
			successToast('Successfully assigned role to user');
		},
	});

	const assignUseRolesMutation = useMutation(UsersApi.assignUseRoleToUser, {
		onSuccess: (data) => {
			queryClient.invalidateQueries(['assignedRoles', userUuid]);
			successToast('Successfully added user to tenant');
		},
	});

	const removeRolesMutation = useMutation(UsersApi.unassignRolesFromUser, {
		onSuccess: (data) => {
			queryClient.invalidateQueries(['assignedRoles', userUuid]);
			successToast('Successfully removed role from user');
		},
	});

	const isLoading =
		isLoadingAssignedRoles ||
		isLoadingAvailableRoles ||
		isLoadingTenants ||
		assignRolesMutation.isLoading ||
		assignUseRolesMutation.isLoading ||
		removeRolesMutation.isLoading;

	const assignedRolesMap: RoleMap = {};

	assignedRoles?.forEach((role) => {
		assignedRolesMap[role.tenantHostname || role.namespace] = role.role;
	});

	const defaultValues = {
		rolesAndPermissionTenants: {},
		legacyAuthTenants: {},
		namespaces: {},
	} as UserDetailRolesShape;

	if (tenants && availableRoles) {
		availableRoles?.forEach((role) => {
			const tenant = tenants.find(
				(tenant) => tenant.hostname === role.tenantHostname,
			);
			if (tenant?.useRolesAndPermissions) {
				defaultValues.rolesAndPermissionTenants[role.tenantHostname] =
					!!assignedRolesMap[role.tenantHostname];
			}
			switch (role.accountType) {
				case INTERNAL_ADMIN__ACCOUNT_TYPE:
					defaultValues.namespaces[role.namespace] =
						!!assignedRolesMap[role.namespace];
					break;
				case DEPRECATED__CLAIMS_ONLY_USER__ACCOUNT_TYPE:
				case DEPRECATED__EMPLOYEE__ACCOUNT_TYPE:
				case DEPRECATED__READ_ONLY_USER__ACCOUNT_TYPE:
					if (!tenant?.useRolesAndPermissions) {
						if (!defaultValues.legacyAuthTenants[role.tenantHostname]) {
							defaultValues.legacyAuthTenants[role.tenantHostname] =
								assignedRolesMap[role.tenantHostname] || '';
						}
					}
					break;
			}
		});
	}

	const editUserMigration = useMutation(UsersApi.updateUser, {
		onSuccess: (data) => {
			const currentUsers = queryClient.getQueryData<UserInfo[]>([USER_KEY]);
			if (currentUsers) {
				// replace existing user with updated user
				const updatedUsers = currentUsers.map((user) => {
					if (user.userUuid === data.userUuid) {
						return data;
					} else {
						return user;
					}
				});

				queryClient.invalidateQueries([USER_KEY]);
				queryClient.setQueryData([USER_KEY], updatedUsers);
			}
			queryClient.setQueryData([USER_KEY, data.userUuid], () => data);
			successToast('Successfully updated user details');
		},
	});

	const deleteUserMigration = useMutation(UsersApi.deleteUser, {
		onSuccess: () => {
			// Remove user from list of users
			const currentUsers = queryClient.getQueryData<UserInfo[]>([USER_KEY]);
			const updatedUsers = currentUsers.filter(
				(user) => user.userUuid !== userUuid,
			);
			queryClient.setQueriesData([USER_KEY], updatedUsers);

			// Refresh the clients list
			queryClient.invalidateQueries([USER_KEY]);

			// Remove this client from cache
			queryClient.setQueryData([USER_KEY, userUuid], null);

			successToast('Successfully deleted user');

			setIsDeleteConfirmationModalOpen(false);
			navigate('/admin/users');
		},
		onError: (err) => {
			errorToast('Error deleting user');
		},
	});

	const handleDeleteUser = async () => {
		deleteUserMigration.mutate(userUuid);
	};

	const handleEditUser: SubmitHandler<UserRequest> = async (values) => {
		await editUserMigration.mutateAsync(values);
		setIsEditConfirmationModalOpen(false);
	};

	const handleTenantAssignment = (
		tenantType:
			| 'rolesAndPermissionTenants'
			| 'legacyAuthTenants'
			| 'namespaces',
		role: string,
		enable: boolean,
		previousRole?: string,
	) => {
		if (tenantType === 'legacyAuthTenants' && previousRole !== '') {
			removeRolesMutation.mutate({
				userUuid,
				roles: [previousRole],
			});
		}
		if (tenantType === 'legacyAuthTenants' && enable) {
			assignRolesMutation.mutate({
				userUuid,
				roles: [role],
			});
		}
		if (tenantType === 'rolesAndPermissionTenants' && enable) {
			assignUseRolesMutation.mutate({
				userUuid,
				roles: [role],
			});
		}
		if (tenantType === 'rolesAndPermissionTenants' && !enable) {
			removeRolesMutation.mutate({
				userUuid,
				roles: [role],
			});
		}
		if (tenantType === 'namespaces' && enable) {
			assignRolesMutation.mutate({
				userUuid,
				roles: [role],
			});
		}
		if (tenantType === 'namespaces' && !enable) {
			removeRolesMutation.mutate({
				userUuid,
				roles: [role],
			});
		}
	};

	const requestSearch = (searchValue: string) => {
		setSearchText(searchValue);
	};

	const filteredLegacyAuthTenantsArray = Object.keys(
		defaultValues?.legacyAuthTenants,
	).filter((tenantHostname) => tenantHostname.includes(searchText));

	const filteredTenantsArray = Object.keys(
		defaultValues?.rolesAndPermissionTenants,
	).filter((tenantHostname) => tenantHostname.includes(searchText));

	const namespacesArray = Object.keys(defaultValues?.namespaces);

	return (
		<Container maxWidth='md'>
			<Paper sx={{ pl: 2, pr: 2, pt: 2, pb: 2 }}>
				<Grid
					container
					justifyContent='space-between'
					alignItems='center'
					sx={{ pb: 2 }}
				>
					<Grid item>
						<Typography variant='h6'>{USER_DETAILS_PAGE__TITLE}</Typography>
					</Grid>
					<Grid item>
						<Tooltip
							title={USER_DETAILS_PAGE__EDIT_USER_BUTTON_TOOLTIP}
							aria-label={USER_DETAILS_PAGE__EDIT_USER_BUTTON_ALT_TEXT}
						>
							<IconButton
								onClick={() => setIsEditConfirmationModalOpen(true)}
								sx={{ minWidth: 0, height: 32, width: 32 }}
								size='small'
							>
								<EditIcon color='inherit' />
							</IconButton>
						</Tooltip>
						{user?.userUuid !== activeUserUuid && (
							<Tooltip
								title={USER_DETAILS_PAGE__DELETE_USER_BUTTON_TOOLTIP}
								aria-label={USER_DETAILS_PAGE__DELETE_USER_BUTTON_ALT_TEXT}
							>
								<IconButton
									onClick={() => setIsDeleteConfirmationModalOpen(true)}
									sx={{ minWidth: 0, height: 32, width: 32 }}
								>
									<DeleteIcon color='inherit' />
								</IconButton>
							</Tooltip>
						)}
					</Grid>
				</Grid>
				<Box sx={{ display: 'flex' }}>
					<Box sx={{ flex: '.5 1 0', pr: 2 }}>
						<Grid container direction='column' sx={{ pt: 2, height: '100%' }}>
							<Grid item sx={{ pb: 2, width: 220, wordWrap: 'break-word' }}>
								{!user ? (
									<Skeleton width={240} height={14} />
								) : (
									<Typography variant='caption' fontWeight={600}>
										{USER_DETAILS_PAGE__NAME_TITLE}
									</Typography>
								)}
								{!user ? (
									<Skeleton width={200} height={32} />
								) : (
									<Typography variant='body2'>
										{user.firstName + ' ' + user.lastName}
									</Typography>
								)}
							</Grid>
							<Grid item sx={{ pb: 2, width: 220, wordWrap: 'break-word' }}>
								{!user ? (
									<Skeleton width={240} height={14} />
								) : (
									<Typography variant='caption' fontWeight={600}>
										{USER_DETAILS_PAGE__EMAIL_TITLE}
									</Typography>
								)}
								{!user ? (
									<Skeleton width={240} height={14} />
								) : (
									<Typography variant='body2'>{user.email}</Typography>
								)}
							</Grid>
							<Grid
								item
								sx={{ flexGrow: 1, width: 220, wordWrap: 'break-word' }}
							>
								{!user ? (
									<Skeleton width={240} height={14} />
								) : (
									<Typography variant='caption' fontWeight={600}>
										{USER_DETAILS_PAGE__USERNAME_TITLE}
									</Typography>
								)}
								{!user ? (
									<Skeleton width={240} height={14} />
								) : (
									<Typography variant='body2'>{user.username}</Typography>
								)}
							</Grid>
							<Grid item>
								<Button
									color='inherit'
									variant='outlined'
									component={Link}
									to='/admin/users'
								>
									{USER_DETAILS_PAGE__BACK_BUTTON}
								</Button>
							</Grid>
						</Grid>
					</Box>
					<Stack
						sx={{
							flex: '1 1 0',
							minHeight: 500,
						}}
						spacing={2}
					>
						<TextField
							variant='outlined'
							size='small'
							fullWidth
							value={searchText}
							onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
								requestSearch(event.target.value)
							}
							placeholder={USER_DETAILS_PAGE__FILTER_TENANT_PLACEHOLDER}
							InputProps={{
								startAdornment: <SearchIcon fontSize='small' />,
								endAdornment: (
									<IconButton
										title='Clear'
										aria-label='Clear'
										size='small'
										style={{
											visibility: searchText ? 'visible' : 'hidden',
										}}
										onClick={() => requestSearch('')}
									>
										<ClearIcon fontSize='small' />
									</IconButton>
								),
							}}
							sx={{
								width: {
									xs: 1,
									sm: 'auto',
								},
								m: (theme) => theme.spacing(1, 0.5, 1.5),
								'& .MuiSvgIcon-root': {
									mr: 0.5,
								},
								'& .MuiInput-underline:before': {
									borderBottom: 1,
									borderColor: 'divider',
								},
							}}
						/>
						<Box
							sx={{
								height: '75vh',
								overflowY: 'auto',
							}}
						>
							{filteredTenantsArray.length === 0 && isLoading && (
								<Skeleton width='100%' height={28} />
							)}

							{filteredTenantsArray.length > 0 && (
								<FormControl
									component='fieldset'
									variant='standard'
									sx={{ mb: 4 }}
								>
									<Typography variant='h6'>
										{USER_DETAILS_PAGE__TENANTS_TITLE}
									</Typography>
									<Typography variant='caption' gutterBottom>
										{
											'To assign permissions for this user, click into a tenant and assign appropriate Roles after assigning the user to a given tenant.'
										}
									</Typography>
									{filteredTenantsArray.map((tenantHostname, index) => (
										<Stack
											key={index}
											spacing={2}
											direction='row'
											justifyContent='space-between'
										>
											<FormGroup>
												<FormControlLabel
													control={
														<Switch
															disabled={isLoading}
															checked={
																defaultValues.rolesAndPermissionTenants[
																	tenantHostname
																]
															}
															onChange={(event) =>
																handleTenantAssignment(
																	'rolesAndPermissionTenants',
																	`$${USE_ROLE_ACCOUNT_TYPE}@${tenantHostname}`,
																	event.target.checked,
																)
															}
															name={tenantHostname}
														/>
													}
													label={
														defaultValues.rolesAndPermissionTenants[
															tenantHostname
														] ? (
															<MuiLink
																component={Link}
																to={`/admin/tenants/${tenantHostname}/users`}
															>
																{tenantHostname}
															</MuiLink>
														) : (
															tenantHostname
														)
													}
												/>
											</FormGroup>
										</Stack>
									))}
								</FormControl>
							)}
							{filteredLegacyAuthTenantsArray.length === 0 && isLoading && (
								<Skeleton width='100%' height={28} />
							)}
							{filteredLegacyAuthTenantsArray.length > 0 && (
								<FormControl
									component='fieldset'
									variant='standard'
									sx={{ mb: 4 }}
								>
									<Typography variant='h6' gutterBottom>
										{USER_DETAILS_PAGE__LEGACY_AUTH_TENANT_TITLE}
									</Typography>
									{filteredLegacyAuthTenantsArray.map(
										(tenantHostname, index) => (
											<Stack
												key={index}
												direction='column'
												justifyContent='space-between'
												sx={{ mb: 2 }}
											>
												<Typography>{tenantHostname}</Typography>
												<ToggleButtonGroup
													disabled={isLoading}
													color='primary'
													size='small'
													value={
														defaultValues.legacyAuthTenants[tenantHostname]
													}
													exclusive
													onChange={(e, value) =>
														handleTenantAssignment(
															'legacyAuthTenants',
															value,
															!!value,
															defaultValues.legacyAuthTenants[tenantHostname],
														)
													}
												>
													<ToggleButton
														value={`$${DEPRECATED__CLAIMS_ONLY_USER__ACCOUNT_TYPE}@${tenantHostname}`}
													>
														{LEGACY_AUTH_TENANT_CLAIM_ONLY_LABEL}
													</ToggleButton>
													<ToggleButton
														value={`$${DEPRECATED__READ_ONLY_USER__ACCOUNT_TYPE}@${tenantHostname}`}
													>
														{LEGACY_AUTH_TENANT_READ_ONLY_LABEL}
													</ToggleButton>
													<ToggleButton
														value={`$${DEPRECATED__EMPLOYEE__ACCOUNT_TYPE}@${tenantHostname}`}
													>
														{LEGACY_AUTH_TENANT_EMPLOYEE_LABEL}
													</ToggleButton>
												</ToggleButtonGroup>
											</Stack>
										),
									)}
								</FormControl>
							)}
							{namespacesArray.length > 0 && (
								<FormControl component='fieldset' variant='standard'>
									<Typography variant='h6' gutterBottom>
										{USER_DETAILS_PAGE__NAMESPACE_TITLE}
									</Typography>
									{Object.keys(defaultValues?.namespaces).map(
										(namespace, index) => (
											<FormGroup key={index}>
												<FormControlLabel
													control={
														<Switch
															disabled={
																isLoading || user?.userUuid === activeUserUuid
															}
															checked={defaultValues.namespaces[namespace]}
															onChange={(event) =>
																handleTenantAssignment(
																	'namespaces',
																	`#namespace.owner@${namespace}`,
																	event.target.checked,
																)
															}
															name={namespace}
														/>
													}
													label={namespace}
												/>
												{user?.userUuid === activeUserUuid && (
													<FormHelperText>
														{USER_DETAILS_PAGE__NAMESPACE_TITLE_EXPLAINER}
													</FormHelperText>
												)}
											</FormGroup>
										),
									)}
								</FormControl>
							)}
							{namespacesArray.length === 0 && isLoading && (
								<Skeleton width='100%' height={28} />
							)}
						</Box>
					</Stack>
				</Box>
			</Paper>
			<ConfirmationModal
				headerText={USER_DETAILS_PAGE__DELETE_CONFIRMATION_TEXT}
				bodyText={USER_DETAILS_PAGE__DELETE_CONFIRMATION_TEXT_2}
				open={isDeleteConfirmationModalOpen}
				handleClose={() => setIsDeleteConfirmationModalOpen(false)}
				handleSubmit={handleDeleteUser}
			/>
			<Dialog
				open={isEditConfirmationModalOpen}
				onClose={() => setIsEditConfirmationModalOpen(false)}
			>
				<DialogTitle>{USER_DETAILS_PAGE__EDIT_USER_MODAL_TITLE}</DialogTitle>
				<DialogContent sx={{ width: 600 }}>
					<UsersForm
						initialValues={user}
						isUpdate={true}
						isLoading={editUserMigration.isLoading}
						handleSubmit={handleEditUser}
						handleClose={() => setIsEditConfirmationModalOpen(false)}
					/>
				</DialogContent>
			</Dialog>
		</Container>
	);
};

export default AdministrationUserDetails;
